import { useGetWorkspacesQuery } from '@carboncopyco/data';
import type { IDemo, Play, Playbook, TopAssetType } from '@carboncopyco/types';
import {
  getDateFromDaysAgo,
  getNowInFormat,
  getTimezoneName,
} from '@carboncopyco/utils';
import constate from 'constate';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

const timezone = getTimezoneName();

export type ConsolePageProps = {
  previewDemo: (demo: IDemo, queryString: string) => void;
  previewTour: (tourId: string, workspaceId: string) => void;
  previewPlaybook: (playbookId: string, workspaceId: string) => void;
  goToAssetPage: (
    asset: {
      id: string;
      workspaceId: string;
      type: TopAssetType;
    },
    options?: { tab?: string }
  ) => void;
  handlePlay: (play: Play) => void;
  goToNotificationSettings: () => void;
  goToAnalyticsDashboard: () => void;
  goToIntegrationSettings: () => void;
  notificationSettingsRoute?: string;
  goToRecorder: () => void;
  createTourFromLiveApp: ({
    appUrl,
    playerToken,
    fileServiceToken,
    tourId,
    originalTourId,
    insertIndex,
  }: {
    appUrl: string;
    playerToken?: string;
    fileServiceToken?: string;
    tourId?: string;
    originalTourId?: string;
    insertIndex?: number;
  }) => void;
  createTourFromDemo: (demoId: number, tourId: number) => void;
  createOverlay: () => void;
  goToTeamActivity: () => void;
  openGiveADemoModal?: (demo?: IDemo) => void;
  openGiveAPlaybookModal?: (playbook: Playbook) => void;
};

const useConsole = (
  props: ConsolePageProps & {
    period: number;
    setPeriod: (period: number) => void;
  }
) => {
  const { period } = props;
  const { organizationId } = useParams();
  const { data: userWorkspaces } = useGetWorkspacesQuery(
    { organizationId },
    { skip: !organizationId }
  );
  const startTime = useMemo(() => getDateFromDaysAgo(period), [period]);
  const endTime = useMemo(() => getNowInFormat('YYYY-MM-DDTHH:mm:ssZ'), []);
  const prevStartTime = useMemo(() => getDateFromDaysAgo(period * 2), [period]);

  return {
    ...props,
    timezone,
    prevStartTime,
    prevEndTime: startTime,
    startTime,
    endTime,
    userWorkspaces,
  };
};

export const [ConsoleProvider, useConsoleState] = constate(useConsole);
